import React, { useEffect, useState } from 'react';
import { Button, Table, Pagination, OverlayTrigger, Tooltip, Badge } from 'react-bootstrap';
import AprovacaoModal from './Aprovacaomodal';
import ContaServicoViewModel, { SolicitacaoContaServico, PaginatedResponse } from '../../../viewmodel/ContaServicoViewModel';
import useUnidadeStore from '../../../stores/unidadeStore';
import { toast } from 'react-toastify';
import BackendErrorMessage from '../../../components/BackendErrorMessage';
import api from '../../../api';

const SolicitacoesPendentes: React.FC = () => {
  const { unidades, fetchUnidades } = useUnidadeStore();
  const [solicitacoes, setSolicitacoes] = useState<PaginatedResponse<SolicitacaoContaServico>>({
    content: [],
    pageable: { pageNumber: 0, pageSize: 10 },
    totalElements: 0,
    totalPages: 0,
    last: false
  });
  const [selected, setSelected] = useState<SolicitacaoContaServico | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        await fetchUnidades();
        const solicitacoesData = await ContaServicoViewModel.getSolicitacoesPendentes(currentPage, 10);
        setSolicitacoes(solicitacoesData);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro ao carregar dados');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentPage, fetchUnidades]);

  const getUnidadeNome = (unidadeId: string) => {
    const unidade = unidades.find(u => u.id === unidadeId);
    return unidade ? unidade.nome : unidadeId;
  };

  const [permissoesPerfil, setPermissoesPerfil] = useState<Record<string, string[]>>({});

  const fetchPermissoesPerfil = async (perfilId: string) => {
    if (permissoesPerfil[perfilId]) return;
    try {
      const response = await api.get(`/perfis/${perfilId}`);
      setPermissoesPerfil(prev => ({
        ...prev,
        [perfilId]: response.data.permissoes?.map((p: any) => p.nome) || []
      }));
    } catch (err) {
      console.error('Erro ao buscar permissões:', err);
    }
  };

  const renderPerfilComPermissoes = (s: SolicitacaoContaServico) => {
    if (!s.perfilSolicitado) return '—';
    
    const permissoes = permissoesPerfil[s.perfilSolicitado.id];
    
    return (
      <OverlayTrigger
        placement="right"
        overlay={
          <Tooltip id={`tooltip-${s.id}`}>
            {permissoes ? (
              <div style={{ textAlign: 'left' }}>
                <strong>Permissões:</strong>
                <ul style={{ paddingLeft: '20px', margin: '5px 0' }}>
                  {permissoes.map((p, i) => <li key={i}>{p}</li>)}
                </ul>
              </div>
            ) : 'Carregando...'}
          </Tooltip>
        }
        onEnter={() => fetchPermissoesPerfil(s.perfilSolicitado!.id)}
      >
        <span style={{ cursor: 'pointer', textDecoration: 'underline dotted' }}>
          {s.perfilSolicitado.nome} <Badge bg="secondary">{permissoes?.length || '?'}</Badge>
        </span>
      </OverlayTrigger>
    );
  };

  const handleSuccess = async () => {
    // Recarrega a página atual após aprovação/rejeição
    try {
      await ContaServicoViewModel.getSolicitacoesPendentes(currentPage, 10)
        .then(data => setSolicitacoes(data))
        .catch(err => setError(err instanceof Error ? err.message : 'Erro ao recarregar solicitações'));
      setSelected(null);
      toast.success("Conta de Serviço criada. Aperte F5 para atualizar a página")
    }
    catch(error: any){
      toast.error(BackendErrorMessage(error))
    }    
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  if (loading) {
    return <div>Carregando...</div>;
  }

  if (error) {
    return <div className="text-danger">{error}</div>;
  }

  return (
    <>
      <h4>Solicitações Pendentes</h4>
      <Table striped bordered hover>
        <thead>
          <tr>
            <th>Descrição</th>
            <th>Unidade</th>
            <th>Perfil</th>
            <th>Acesso Multi-Unidade</th>
            <th>Data Expiração</th>
            <th>Ações</th>
          </tr>
        </thead>
        <tbody>
          {solicitacoes.content.map(s => (
            <tr key={s.id}>
              <td>{s.descricao}</td>
              <td>{getUnidadeNome(s.unidadeId)}</td>
              <td>{renderPerfilComPermissoes(s)}</td>
              <td>{s.acessoMultiUnidadeSolicitado ? 'Sim' : 'Não'}</td>
              <td>{s.dataExpiracaoSolicitada || '—'}</td>
              <td>
                <Button size="sm" onClick={() => setSelected(s)}>Avaliar</Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {solicitacoes.totalPages > 1 && (
        <Pagination>
          <Pagination.First onClick={() => handlePageChange(0)} disabled={currentPage === 0} />
          <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 0} />

          {[...Array(solicitacoes.totalPages)].map((_, index) => (
            <Pagination.Item
              key={index}
              active={index === currentPage}
              onClick={() => handlePageChange(index)}
            >
              {index + 1}
            </Pagination.Item>
          ))}

          <Pagination.Next
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === solicitacoes.totalPages - 1}
          />
          <Pagination.Last
            onClick={() => handlePageChange(solicitacoes.totalPages - 1)}
            disabled={currentPage === solicitacoes.totalPages - 1}
          />
        </Pagination>
      )}

      {selected && (
        <AprovacaoModal
          solicitacao={{
            id: selected.id,
            descricao: selected.descricao,
            unidadeId: selected.unidadeId,
            unidadeNome: getUnidadeNome(selected.unidadeId),
            acessoMultiUnidadeSolicitado: selected.acessoMultiUnidadeSolicitado,
            perfilSolicitado: selected.perfilSolicitado?.id,
            perfilSolicitadoNome: selected.perfilSolicitado?.nome,
          }}
          onClose={() => setSelected(null)}
          onSuccess={handleSuccess}
        />
      )}
    </>
  );
};

export default SolicitacoesPendentes;